import * as React from "react";
import {cx} from "emotion";

import "./permissions-menu.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {scopedState}    from "@Common/react/scoped-state";
import {UseState}       from "@Common/react/use-state";
import {Select2}        from "@Common/ui-components/select-2/select-2";
import {equalDeep}      from "@Common/utils/objects";

import {AGENT_LEVEL, BRANCH_LEVEL, COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";

import {MenuLayout} from "../menu-layout/menu-layout";
import {LayoutLoaderContext} from "../../../../user-assets/common/layout/user-assets-layout-loader";

export const PermissionsMenu = ({user, readOnly, dataUpdated}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("userLevels"),
    ["uploading", (_, next) => UseState({next})],
    ["permissionsState", (_, next) => user.value && UseState({
        _key: "security",
        next,
        initValue: defaultState({user})
    })],
    ["loader", (_, next) => (
        <LayoutLoaderContext.Consumer>
            {next}
        </LayoutLoaderContext.Consumer>
    )],
    ["headerData", ({loader}, next) => loader.loadHeaderData({agentId: null, next})],
    ({apis, uploading, permissionsState, loader}, next) => MenuLayout({
        title: "Permissions",
        button: {
            onClick: async () => {
                uploading.onChange(true);
                const result = await apis.next.userProfile.updateUserProfile({userId: user.value.id, loginLevel: permissionsState.value.loginLevel});
                if (result?.errorMessage) {
                    alert(result.errorMessage);
                } else if (result?.errorInfo) {
                    const errors = JSON.parse(result.errorInfo);
                    errors.map((error) => {
                        scopedState(permissionsState, ['errors', error.mapping]).onChange(error.message)
                    });
                } else {
                    dataUpdated.onChange(false);
                    await user.reload();
                }
                uploading.onChange(false);
            },
            disabled: readOnly?.inactive || readOnly?.companyAdmin || uploading.value || !dataUpdated.value,
        },
        next,
    }),
    ({permissionsState, userLevels}) => (
        <div className="permissions-menu-w9n">
            <div className={cx("input-card", {"read-only": readOnly?.inactive})}>
                <div className="label" xd-qa-type="label" xd-qa-name="Select an Admin Role">Select an Admin Role</div>
                {RoleSelect({userLevel: userLevels.userLevel, inputProps:{
                    disabled: readOnly?.inactive || readOnly?.companyAdmin,
                    value: scopedState(permissionsState, ['loginLevel']).value,
                    isSelected: (i) => i.value === scopedState(permissionsState, ['loginLevel']).value,
                    onSelect: (i) => {
                        dataUpdated.onChange(!equalDeep(defaultState({user}).loginLevel, i.value));
                        scopedState(permissionsState, ['loginLevel']).onChange(i?.value ?? "");
                    },
                    "xd-qa-name": "Office Admin",
                }})}
            </div>
            <div className="input-card">
                {permissionsState.value.loginLevel === AGENT_LEVEL && <div className="description" xd-qa-type="label" xd-qa-name="User Level Description">
                    Admin roles give users additional permissions
                </div>}
                {permissionsState.value.loginLevel === BRANCH_LEVEL && <div className="description" xd-qa-type="label" xd-qa-name="Office Level Description">
                    "Office Admin" role will provide the user with office-level access, including:
                    <ul className="list">
                        <li className="list-item" xd-qa-type="label" xd-qa-name="The ability to place orders for the office">The ability to place orders for the office</li>
                        <li className="list-item" xd-qa-type="label" xd-qa-name="Access to office billing">Access to office billing</li>
                        <li className="list-item" xd-qa-type="label" xd-qa-name="The ability to modify other user accounts">The ability to modify other user accounts</li>
                    </ul>
                </div>}
                {userLevels.userLevel === COMPANY_LEVEL && permissionsState.value.loginLevel === COMPANY_LEVEL && (
                    <div className="description" xd-qa-type="label" xd-qa-name="Company Level Description">
                        "Company Admin" role will provide the user with company-level access, including:
                        <ul className="list">
                            <li className="list-item" xd-qa-type="label" xd-qa-name="The ability to place orders for the office">
                                The ability to place orders for the office
                            </li>
                            <li className="list-item" xd-qa-type="label" xd-qa-name="Access to office billing">
                                Access to office billing
                            </li>
                            <li className="list-item" xd-qa-type="label" xd-qa-name="The ability to modify other user accounts">
                                The ability to modify other user accounts
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    )
);

const defaultState = ({user}) => {
    let inputs = {loginLevel: user?.value.loginLevel,
        userId: user?.value.id}
    return inputs
};

const RoleSelect = ({inputProps, userLevel}) => Select2({
    ...inputProps,
    getLabel: (i) => i.label,
    placeholder: "Select...",
    className: "select",
    list: roles({userLevel}),
    disableClearingSelection: true,
    "xd-qa-type":"dropdown",
    xdQaName: "Admin Role",
});

const roles = ({userLevel}) => [
    {
        label: "None",
        value: AGENT_LEVEL,
    },
    {
        label: "Office Admin",
        value: BRANCH_LEVEL,
    },
    userLevel === COMPANY_LEVEL && {
        label: "Company Admin",
        value: COMPANY_LEVEL,
    },
];

